import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/feature/Layout';
import { fetchSitesList } from '../../repositories/sites';
import type { Site, SiteSortKey, SiteFilter } from '../../types/sites';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

type SiteWithRating = Site & {
  site_reviews?: Array<{
    id: string;
    status: string;
    is_published: boolean;
    rating_place: number;
    rating_home: number;
    rating_self: number;
    rating_print: number;
    rating_design: number;
  }>;
  avgRating?: number | null;
  reviewCount?: number;
};

export default function SitesPage() {
  // ソート・フィルター用の state
  const [sortKey, setSortKey] = useState<SiteSortKey>('recommended');
  const [filter, setFilter] = useState<SiteFilter>({
    freeLevels: [],
    minRating: undefined,
    tags: [],
  });
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');

  const [sites, setSites] = useState<SiteWithRating[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SEO設定
  useSEO({
    title: '教材サイト一覧｜キョウコレ',
    description: '学習支援に役立つ教材サイトを検索できます。無料・有料、教科別、タグ別に絞り込んで、目的に合った教材サイトを見つけましょう。',
    keywords: '教材サイト,学習支援,無料教材,有料教材,オンライン学習,教材検索',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/sites',
    jsonLd: generateWebPageSchema(
      '教材サイト一覧｜キョウコレ',
      '学習支援に役立つ教材サイトを検索できます。無料・有料、教科別、タグ別に絞り込んで、目的に合った教材サイトを見つけましょう。',
      '/sites'
    ),
  });

  // キーワードのdebounce処理
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword);
    }, 300);

    return () => clearTimeout(timer);
  }, [keyword]);

  // 一覧取得
  const loadSites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSitesList({
        sort: sortKey,
        filter,
        keyword: debouncedKeyword,
        limit: 50,
        offset: 0,
      });

      // レビュー平均を計算
      const withRating = data.map((site: any) => {
        const reviews = site.site_reviews || [];
        const approvedReviews = reviews.filter(
          (r: any) => r.status === 'approved' && r.is_published === true
        );

        if (approvedReviews.length === 0) {
          return { ...site, avgRating: null, reviewCount: 0 };
        }

        // 各レビューの平均を計算
        const reviewAvgs = approvedReviews.map((r: any) => {
          return (r.rating_place + r.rating_home + r.rating_self + r.rating_print + r.rating_design) / 5;
        });

        // サイトの平均を計算
        const avgRating = reviewAvgs.reduce((sum, avg) => sum + avg, 0) / reviewAvgs.length;

        return {
          ...site,
          avgRating,
          reviewCount: approvedReviews.length,
        };
      });

      setSites(withRating);
    } catch (e: any) {
      console.error('Failed to load sites:', e);
      setError(e.message ?? '教材サイトの読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [sortKey, filter, debouncedKeyword]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  // タグの一覧を取得（ユニーク値）
  const allTags = sites.flatMap(site => site.tags || []);
  const uniqueTags = Array.from(new Set(allTags));

  // フィルター変更ハンドラ
  const handleFreeLevelChange = (level: '完全無料' | '一部無料') => {
    setFilter((prev) => {
      const exists = prev.freeLevels?.includes(level);
      const freeLevels = exists
        ? prev.freeLevels!.filter((v) => v !== level)
        : [...(prev.freeLevels ?? []), level];
      return { ...prev, freeLevels };
    });
  };

  const handleMinRatingChange = (value: string) => {
    const num = value ? Number(value) : undefined;
    setFilter((prev) => ({ ...prev, minRating: num }));
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFilter((prev) => ({ ...prev, tags: selectedOptions }));
  };

  const handleSortChange = (value: string) => {
    setSortKey(value as SiteSortKey);
  };

  // URLからドメインを取得する関数
  const getDomain = (url: string): string => {
    try {
      const u = new URL(url.startsWith('http') ? url : `https://${url}`);
      return u.hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  };

  // サムネイル画像のURLを取得する関数
  const getThumbnailUrl = (site: Site): string => {
    if (site.thumbnail_url) {
      return site.thumbnail_url;
    }
    
    if (site.base_url) {
      const domain = getDomain(site.base_url);
      return `https://www.google.com/s2/favicons?sz=128&domain=${domain}`;
    }
    
    return '';
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">教材サイト一覧</h1>

          {/* キーワード検索 */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="サイト名・説明・URLで検索"
                className="w-full px-4 py-3 pl-12 rounded-xl border border-slate-300 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
              />
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl"></i>
              {keyword && (
                <button
                  onClick={() => setKeyword('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <i className="ri-close-circle-fill text-xl"></i>
                </button>
              )}
            </div>
          </div>

          {/* コントロールバー */}
          <div className="mb-6 bg-white rounded-xl shadow-sm border border-slate-100 p-4 flex flex-col md:flex-row gap-4 md:items-start md:justify-between">
            {/* 左側：絞り込み */}
            <div className="flex-1 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                  利用料金
                </label>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filter.freeLevels?.includes('完全無料')}
                      onChange={() => handleFreeLevelChange('完全無料')}
                      className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                    />
                    <span className="ml-2 text-sm text-slate-700">完全無料</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filter.freeLevels?.includes('一部無料')}
                      onChange={() => handleFreeLevelChange('一部無料')}
                      className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                    />
                    <span className="ml-2 text-sm text-slate-700">一部無料</span>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="min-rating" className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                  最低評価
                </label>
                <select
                  id="min-rating"
                  value={filter.minRating ?? ''}
                  onChange={(e) => handleMinRatingChange(e.target.value)}
                  className="mt-1 block w-full rounded-md border-slate-300 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500 cursor-pointer"
                >
                  <option value="">指定なし</option>
                  <option value="3">★3以上</option>
                  <option value="4">★4以上</option>
                </select>
              </div>

              {uniqueTags.length > 0 && (
                <div>
                  <label htmlFor="tags-filter" className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                    タグ
                  </label>
                  <select
                    id="tags-filter"
                    multiple
                    value={filter.tags ?? []}
                    onChange={handleTagsChange}
                    className="mt-1 block w-full rounded-md border-slate-300 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500 cursor-pointer"
                    size={Math.min(uniqueTags.length, 5)}
                  >
                    {uniqueTags.map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500">Ctrl/Cmd を押しながら複数選択できます</p>
                </div>
              )}
            </div>

            {/* 右側：ソート順 */}
            <div className="md:w-64">
              <label htmlFor="sort-select" className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                並び順
              </label>
              <select
                id="sort-select"
                value={sortKey}
                onChange={(e) => handleSortChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-slate-300 text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500 cursor-pointer"
              >
                <option value="recommended">おすすめ順</option>
                <option value="rating_desc">評価が高い順</option>
                <option value="newest">新着順</option>
                <option value="name_asc">サイト名（あいうえお）</option>
              </select>
            </div>
          </div>

          {/* 検索結果件数 */}
          {!loading && !error && (
            <div className="mb-4 text-sm text-slate-600">
              {sites.length}件の教材サイトが見つかりました
            </div>
          )}

          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              <p className="mt-4 text-gray-600">読み込み中…</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {!loading && !error && sites.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 text-center">
              <i className="ri-search-line text-6xl text-gray-400 mb-4"></i>
              <p className="text-gray-600 mb-2">条件に合う教材サイトが見つかりませんでした。</p>
              <p className="text-sm text-gray-500">絞り込み条件を少しゆるめて再度お試しください。</p>
            </div>
          )}

          {!loading && !error && sites.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sites.map((site) => {
                const thumbnailUrl = getThumbnailUrl(site);
                
                return (
                  <div
                    key={site.id}
                    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 overflow-hidden"
                  >
                    {/* サムネイル枠 */}
                    <div className="aspect-video w-full bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center relative">
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt={`${site.name} のサムネイル`}
                          className="w-full h-full object-contain p-8"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              const fallback = document.createElement('div');
                              fallback.className = 'text-center';
                              fallback.innerHTML = `
                                <div class="text-4xl mb-1">🌐</div>
                                <span class="text-xs text-gray-400">サイトイメージ</span>
                              `;
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      ) : (
                        <div className="text-center">
                          <div className="text-4xl mb-1">🌐</div>
                          <span className="text-xs text-gray-400">サイトイメージ</span>
                        </div>
                      )}
                      
                      {/* 無料/有料バッジ（右上） */}
                      {site.free_level && (
                        <div className="absolute top-2 right-2">
                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 border border-emerald-100">
                            {site.free_level === '完全無料' && '無料'}
                            {site.free_level === '一部無料' && '一部無料'}
                            {site.free_level === '有料' && '有料'}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <h2 className="text-base font-semibold text-gray-900 line-clamp-2 flex-1">
                          {site.name}
                        </h2>
                        {/* レビュー平均★ */}
                        {site.avgRating != null && site.reviewCount > 0 && (
                          <div className="flex items-center gap-1 text-xs whitespace-nowrap">
                            <i className="ri-star-fill text-yellow-400"></i>
                            <span className="font-semibold text-gray-900">{site.avgRating.toFixed(1)}</span>
                            <span className="text-gray-500">({site.reviewCount})</span>
                          </div>
                        )}
                      </div>
                      
                      {site.description && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                          {site.description.slice(0, 80)}
                          {site.description.length > 80 ? '…' : ''}
                        </p>
                      )}

                      {site.tags && site.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {site.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700 border border-teal-100"
                            >
                              {tag}
                            </span>
                          ))}
                          {site.tags.length > 3 && (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                              +{site.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      <Link
                        to={`/sites/${site.slug}`}
                        className="inline-block w-full text-center px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        詳細を見る
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
